import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, FuntourMark, Spinner, TextField } from '@rooming/ui';
import { isProblem, useCountdown } from '@rooming/web-core';
import { useState, type SyntheticEvent } from 'react';
import { passengerApi } from '../api';
import { messageOf } from '../messages';

// The token lives in the URL fragment (`/activar#<token>`): the browser never sends it to a server, it is not logged and it
// does not travel in a Referer. It is read once and goes in the body of the two calls.
const tokenFromLocation = (): string => window.location.hash.replace(/^#/, '');

/** Problems that say the LINK itself is unusable (as opposed to a mistake in the form). */
const LINK_PROBLEMS = new Set([
  'enlace-de-activacion-invalido',
  'enlace-de-activacion-vencido',
  'enlace-de-activacion-completo',
]);

/**
 * Activation through the group link (owner, 2026-10-29; DR-405): document + email + password, then the person is signed in
 * and goes straight to choose a room. The server answers the same way whatever the reason a document does not match, so
 * this page cannot be used to find out who is in the group.
 */
export function ActivationPage() {
  const queryClient = useQueryClient();
  const [token] = useState(tokenFromLocation);
  const [document, setDocument] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [waitSeconds, setWaitSeconds] = useCountdown();
  const mismatch = repeat !== '' && repeat !== password;

  const preview = useQuery({
    queryKey: ['activation-preview', token],
    queryFn: async () => (await passengerApi.previewActivation(token)).data,
    enabled: token !== '',
    retry: false,
    staleTime: Infinity,
  });

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault();
    if (mismatch) return;
    setError(undefined);
    setFieldErrors({});
    setBusy(true);
    try {
      await passengerApi.activateAccount({ token, document, password, email });
      setPassword('');
      setRepeat('');
      // The server already signed the person in: leave the link behind and let the app load the session.
      window.history.replaceState(null, '', '/');
      queryClient.setQueryData(['session'], 'ACTIVE');
    } catch (problem) {
      if (isProblem(problem) && problem.errors.length > 0) {
        const byField: Record<string, string> = {};
        for (const field of problem.errors) {
          const name = field.pointer.split('/').pop() ?? 'form';
          byField[name] = byField[name] === undefined ? field.detail : `${byField[name]} ${field.detail}`;
        }
        setFieldErrors(byField);
      } else {
        setError(messageOf(problem));
      }
      if (isProblem(problem) && problem.retryAfterSeconds !== undefined) setWaitSeconds(problem.retryAfterSeconds);
    } finally {
      setBusy(false);
    }
  }

  const linkUnusable =
    token === '' || (preview.isError && isProblem(preview.error) && LINK_PROBLEMS.has(preview.error.slug));
  return (
    <div className="mx-auto w-full max-w-md py-2 sm:py-8">
      <div className="rounded-3xl border border-line bg-surface p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-center gap-3">
          <FuntourMark size={44} />
          <b className="text-lg">Funtour</b>
        </div>
        <p className="text-sm font-bold tracking-widest text-brand-700 uppercase">Bienvenido</p>
        <h1 className="mt-2 text-3xl font-bold">Activá tu cuenta</h1>
        {preview.isPending && token !== '' ? (
          <div className="mt-6">
            <Spinner />
          </div>
        ) : linkUnusable ? (
          <div className="mt-4 flex flex-col gap-4">
            <Alert tone="warning" title="Este enlace no está disponible">
              {preview.isError
                ? messageOf(preview.error)
                : 'El enlace está incompleto. Pedile a tu guía que te lo mande de nuevo.'}{' '}
              Si ya activaste tu cuenta, ingresá con tu usuario o tu documento y tu contraseña. Si no, pedile ayuda a tu
              guía o a Funtour.
            </Alert>
            <a className="font-semibold text-brand-700 underline" href="/">
              Ir a ingresar
            </a>
          </div>
        ) : (
          <>
            <p className="mt-2 mb-6 text-base text-muted">
              {preview.data === undefined ? '' : `Grupo ${preview.data.groupName}. `}
              Escribí tu documento, elegí una contraseña y dejá tu correo. Después elegís tu habitación.
            </p>
            <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
              <TextField
                label="Documento"
                name="document"
                hint="El mismo con el que te inscribieron, con o sin puntos ni guion."
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                required
                value={document}
                error={fieldErrors.document}
                onChange={(event) => {
                  setDocument(event.target.value);
                }}
              />
              <TextField
                label="Correo electrónico"
                name="email"
                type="email"
                hint="Para avisarte de tu habitación. No lo compartimos."
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={email}
                error={fieldErrors.email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
              <TextField
                type="password"
                label="Contraseña"
                name="password"
                autoComplete="new-password"
                hint="Al menos 8 caracteres. Puede ser una frase. No uses tu documento."
                required
                value={password}
                error={fieldErrors.password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
              <TextField
                type="password"
                label="Repetí la contraseña"
                name="repeatPassword"
                autoComplete="new-password"
                required
                value={repeat}
                error={mismatch ? 'Las contraseñas no coinciden.' : undefined}
                onChange={(event) => {
                  setRepeat(event.target.value);
                }}
              />
              {error !== undefined && (
                <Alert tone="error">
                  {error}
                  {waitSeconds > 0 && ` (${String(waitSeconds)} s)`}
                </Alert>
              )}
              <Button
                type="submit"
                fullWidth
                loading={busy}
                disabled={
                  waitSeconds > 0 ||
                  mismatch ||
                  document.trim() === '' ||
                  email.trim() === '' ||
                  password === '' ||
                  repeat === ''
                }
              >
                Activar mi cuenta
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
